// frontend/src/services/changeLog.service.ts
import {
    collection,
    doc,
    getDoc,
    setDoc,
    serverTimestamp,
    query,
    where,
    getDocs,
    Timestamp
  } from 'firebase/firestore';
  import { db } from './firebase';
  
  export interface ChangeLogEntry {
    version: string;
    date: Date;
    title: string;
    category: 'new' | 'improved' | 'fixed';
    description: string;
    icon?: string;
  }
  
  export interface ChangeLog {
    id: string;
    version: string;
    releaseDate: Date;
    expiryDate: Date;
    changes: ChangeLogEntry[];
    isActive: boolean;
    requiresAcknowledgment: boolean;
  }
  
  export interface UserAcknowledgment {
    userId: string;
    changeLogId: string;
    acknowledgedAt: Date;
    version: string;
  }
  
  class ChangeLogService {
    /**
     * Get active change log that requires acknowledgment
     */
    async getActiveChangeLog(): Promise<ChangeLog | null> {
      try {
        const now = new Date();
        const changeLogsQuery = query(
          collection(db, 'change_logs'),
          where('isActive', '==', true),
          where('requiresAcknowledgment', '==', true)
        );
  
        const snapshot = await getDocs(changeLogsQuery);
        
        for (const doc of snapshot.docs) {
          const data = doc.data();
          const expiryDate = data.expiryDate?.toDate() || new Date();
          
          // Check if change log is still within display period
          if (expiryDate > now) {
            return {
              id: doc.id,
              version: data.version || '1.0.0',
              releaseDate: data.releaseDate?.toDate() || new Date(),
              expiryDate: expiryDate,
              changes: data.changes || [],
              isActive: data.isActive || false,
              requiresAcknowledgment: data.requiresAcknowledgment || false
            };
          }
        }
  
        return null;
      } catch (error) {
        console.error('❌ Error getting active change log:', error);
        return null;
      }
    }
  
    /**
     * Check if user has acknowledged a specific change log
     */
    async hasUserAcknowledged(userId: string, changeLogId: string): Promise<boolean> {
      try {
        const ackRef = doc(db, 'change_log_acknowledgments', `${userId}_${changeLogId}`);
        const ackDoc = await getDoc(ackRef);
        return ackDoc.exists();
      } catch (error) {
        console.error('❌ Error checking acknowledgment:', error);
        return false;
      }
    }
  
    /**
     * Record user acknowledgment
     */
    async acknowledgeChangeLog(
      userId: string,
      changeLogId: string,
      version: string
    ): Promise<boolean> {
      try {
        const ackRef = doc(db, 'change_log_acknowledgments', `${userId}_${changeLogId}`);
        
        await setDoc(ackRef, {
          userId: userId,
          changeLogId: changeLogId,
          version: version,
          acknowledgedAt: serverTimestamp(),
          timestamp: serverTimestamp()
        });
  
        console.log('✅ Change log acknowledged by user:', userId);
        return true;
      } catch (error) {
        console.error('❌ Error recording acknowledgment:', error);
        return false;
      }
    }
  
    /**
     * Check if user needs to see change log
     */
    async shouldShowChangeLog(userId: string): Promise<{
      shouldShow: boolean;
      changeLog: ChangeLog | null;
    }> {
      try {
        const activeChangeLog = await this.getActiveChangeLog();
        
        if (!activeChangeLog) {
          return { shouldShow: false, changeLog: null };
        }
  
        const hasAcknowledged = await this.hasUserAcknowledged(userId, activeChangeLog.id);
        
        return {
          shouldShow: !hasAcknowledged,
          changeLog: activeChangeLog
        };
      } catch (error) {
        console.error('❌ Error checking if should show change log:', error);
        return { shouldShow: false, changeLog: null };
      }
    }
  
    /**
     * Create a new change log (for admins)
     */
    async createChangeLog(
      version: string,
      daysActive: number,
      changes: ChangeLogEntry[]
    ): Promise<string | null> {
      try {
        const now = new Date();
        const expiryDate = new Date(now.getTime() + daysActive * 24 * 60 * 60 * 1000);
  
        const changeLogData = {
          version: version,
          releaseDate: Timestamp.fromDate(now),
          expiryDate: Timestamp.fromDate(expiryDate),
          changes: changes,
          isActive: true,
          requiresAcknowledgment: true,
          createdAt: serverTimestamp()
        };
  
        const docRef = doc(collection(db, 'change_logs'));
        await setDoc(docRef, changeLogData);
  
        console.log('✅ Change log created:', docRef.id);
        return docRef.id;
      } catch (error) {
        console.error('❌ Error creating change log:', error);
        return null;
      }
    }
  }
  
  export const changeLogService = new ChangeLogService();